import { create } from 'zustand';
import api, { apiFetch, buildQuery } from '../lib/api';

// Thin client over news-svc. The front page is composed from three separate
// reads — the top slots, the latest block and the tail — rather than one
// endpoint returning a pre-built page, so the layout can be rearranged from
// the settings without the server having to know what the page looks like.
export const useNewsStore = create((set, get) => ({
  settings: null,
  categories: [],
  top: [],
  latest: [],
  archive: [],
  loading: false,
  error: '',

  // Le notizie visibili dipendono dal destinatario: al cambio di identità
  // l'elenco si rifà. Vedi lib/datiDiSessione.js.
  azzera: () => set({ settings: null, categories: [], top: [], latest: [], archive: [], loading: false, error: '' }),

  loadSettings: async () => {
    const r = await api.get('/news/settings');
    set({ settings: r.data });
    return r.data;
  },

  saveSettings: async (patch) => {
    const r = await api.put('/news/settings', patch);
    set({ settings: r.data });
    return r.data;
  },

  loadCategories: async () => {
    const r = await api.get('/news/categories');
    set({ categories: r.data || [] });
    return r.data || [];
  },

  createCategory: (payload) => api.post('/news/categories', payload),
  updateCategory: (id, payload) => api.put(`/news/categories/${id}`, payload),
  deleteCategory: (id) => api.delete(`/news/categories/${id}`),

  // One pass that fills the whole page. `archive` deliberately excludes what
  // the blocks above already show — a front page that repeats its own lead
  // three rows down looks broken even when every query is correct.
  loadFrontPage: async () => {
    set({ loading: true, error: '' });
    try {
      const settings = get().settings || (await get().loadSettings());
      const [topRes, latestRes] = await Promise.all([
        api.get('/news/top'),
        api.get(`/news${buildQuery({ limit: Math.max(1, settings.latestCount + 3) })}`),
      ]);
      const top = topRes.data || [];
      const shownIds = new Set(top.map((n) => n.id));
      const latest = (latestRes.data || []).filter((n) => !shownIds.has(n.id)).slice(0, settings.latestCount);
      latest.forEach((n) => shownIds.add(n.id));

      let archive = [];
      if (settings.archiveCount > 0) {
        const cats = settings.archiveCategoryIds || [];
        const pages = cats.length
          ? await Promise.all(cats.map((c) =>
              api.get(`/news${buildQuery({ category_id: c, limit: settings.archiveCount })}`)))
          : [await api.get(`/news${buildQuery({ limit: settings.archiveCount + shownIds.size })}`)];
        archive = pages
          .flatMap((p) => p.data || [])
          .filter((n) => !shownIds.has(n.id))
          .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
          .slice(0, settings.archiveCount);
      }
      set({ top, latest, archive, loading: false });
    } catch (e) {
      set({ loading: false, error: e.message || 'Errore nel caricamento delle news' });
    }
  },

  list: (params) => api.get(`/news${buildQuery(params || {})}`),
  get: (id) => api.get(`/news/${id}`),
  create: (payload) => api.post('/news', payload),
  update: (id, payload) => api.put(`/news/${id}`, payload),
  remove: (id) => api.delete(`/news/${id}`),

  requestFeature: (id) => api.post(`/news/${id}/feature-request`, {}),
  featureRequests: () => api.get('/news/feature-requests'),
  approveFeature: (id, days, priority) => api.post(`/news/${id}/feature`, { days, priority }),
  // Re-orders the head without touching anybody's remaining days.
  setFeaturePriority: (id, priority) => api.put(`/news/${id}/feature-priority`, { priority }),
  revokeFeature: (id) => api.delete(`/news/${id}/feature`),

  // Multipart: the JSON Content-Type apiFetch sets by default would make
  // multer reject the body, and it drops the header when it sees FormData.
  uploadAttachment: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await apiFetch('/news/media/file', { method: 'POST', body: fd });
    return r.data;
  },

  uploadCover: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await apiFetch('/news/media', { method: 'POST', body: fd });
    return r.data;
  },
}));

export default useNewsStore;
